<template>
    <legal-page title="Privacy Policy">
        <template #default="{legal}">
            <p>
                This Privacy Policy explains what information {{ legal.company }} ("we," "us," or "our") collects through
                {{ legal.brand }} (the "Services"), how we use and share it, and the choices you have. It is part of our
                <router-link to="/terms">Terms of Use</router-link>.
            </p>

            <h3>1. Information We Collect</h3>
            <h4>Information you provide</h4>
            <ul>
                <li><strong>Account and contact details:</strong> name, e-mail addresses, phone numbers, mailing address, and date of birth.</li>
                <li><strong>Professional details:</strong> certifications and certificate numbers, school district, school, position, and, if you choose to provide it, your salary schedule placement.</li>
                <li><strong>Coursework:</strong> study plans, reflections, applications, uploaded files, signatures, and messages you send to our support team.</li>
                <li><strong>Purchase details:</strong> what you bought and how you paid. Card payments are handled by our payment processor; we receive confirmation and limited card details (such as the last four digits), not your full card number.</li>
            </ul>
            <h4>Information collected automatically</h4>
            <ul>
                <li><strong>Course activity:</strong> lessons completed, time recorded while a course is open, and dates of study activity.</li>
                <li><strong>Device and usage information:</strong> IP address, browser type, pages visited, and the date and time of requests.</li>
                <li><strong>Cookies and browser storage:</strong> we store a login token in your browser to keep you signed in, and we use Google Analytics, which sets cookies to help us understand how the Services are used.</li>
            </ul>
            <h4>Information from others</h4>
            <p>
                We may receive information about you from a school district, organization, or sales representative that arranges your
                enrollment; from partner platforms you connect to us, such as FinalForms for athletic coaching certifications; and from
                financing partners about the status of a payment plan.
            </p>

            <h3>2. How We Use Information</h3>
            <ul>
                <li>to provide courses, track your progress, and issue clock hours, certificates, and completion records;</li>
                <li>to review study plans, reflections, and applications, including with the help of automated tools that assist our staff;</li>
                <li>to process purchases and set up enrollments purchased through a financing partner;</li>
                <li>to enforce our Terms and agreements, including restricting account access for non-payment or unmet requirements;</li>
                <li>to send account messages, and, according to your preferences, notifications, check-ins, and updates by e-mail, text message, or mail;</li>
                <li>to provide support, verify identity, prevent fraud, and secure the Services;</li>
                <li>to understand and improve the Services; and</li>
                <li>to comply with law and keep the records needed to verify past completions.</li>
            </ul>

            <h3>3. How We Share Information</h3>
            <p>We may share your information as follows:</p>
            <ul>
                <li><strong>Organizations involved in your enrollment:</strong> if a school district, organization, or sales representative arranged or paid for your enrollment, they may receive information about your enrollment, progress, and completion.</li>
                <li><strong>Certification partners:</strong> where you take coursework for a partner program, we share completion and certification status with that program.</li>
                <li><strong>Financing partners:</strong> if you choose a payment plan or other financing, we share the information needed to set it up and service it. The partner's handling of that information is also governed by your agreement with that partner.</li>
                <li><strong>Partners:</strong> we may share information with educational, professional, and business partners we work with to offer, deliver, verify, or improve our courses, conferences, certifications, and related services, such as school districts, professional associations, course providers, and other organizations we partner with. When you express interest in a partner's program, product, or service (for example, by requesting information, registering, or selecting it through our Services), we may also share your name and contact information with that partner so they can contact you about it.</li>
                <li><strong>Service providers</strong> that process information on our behalf, such as hosting and file storage, payment processing, e-mail delivery, text message delivery, printed mail, and analytics. They may use the information only to provide services to us.</li>
                <li><strong>Legal and safety reasons:</strong> when required by law or legal process, or to protect the rights, safety, and security of our users, us, or others.</li>
                <li><strong>Business transfers:</strong> as part of a merger, acquisition, or sale of assets, subject to this Policy.</li>
            </ul>

            <h3>4. Your Communication Choices</h3>
            <ul>
                <li><strong>E-mail:</strong> turn off optional e-mail in your account settings or with the unsubscribe link in any optional message. Account messages, such as access codes, password resets, and enrollment and registration confirmations, cannot be turned off while you have an account. Messages about a payment plan come from the financing partner that services it and are governed by your agreement with that partner.</li>
                <li><strong>Text messages:</strong> we text login codes only when you request one, and send account and purchase texts about transactions you started. Notification texts are sent only if you opt in. Reply <strong>STOP</strong> to any text, or change your settings, to stop them. Message and data rates may apply.</li>
                <li><strong>Analytics cookies:</strong> you can block or delete cookies in your browser settings, or use Google's opt-out browser add-on.</li>
            </ul>

            <h3>5. Retention</h3>
            <p>
                We keep account information while your account is active. Because districts, agencies, and partners rely on us to verify
                past completions, we keep completion records, such as clock hours, certificates, and the coursework that supports
                them, for as long as they may be needed for verification, and as required by law. Other information is deleted or
                de-identified when it is no longer needed.
            </p>

            <h3>6. Security</h3>
            <p>
                We use administrative, technical, and physical safeguards designed to protect your information, including encrypted
                connections. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.
            </p>

            <h3>7. Your Rights</h3>
            <p>
                You can review and update much of your information in your account settings. You may also ask us to provide, correct,
                or delete your personal information by e-mailing <a :href="`mailto:${legal.supportEmail}`">{{ legal.supportEmail }}</a>.
                We will verify your request and respond as required by applicable law. We may keep information we need for completion
                records, legal obligations, or legitimate business purposes.
            </p>

            <h3>8. Children</h3>
            <p>
                The Services are intended for adults. We do not knowingly collect personal information from children under 13. If you
                believe a child has provided us information, contact us and we will delete it.
            </p>

            <h3>9. Changes to This Policy</h3>
            <p>
                We may update this Policy from time to time. The effective date above shows when it last changed. If we make material
                changes, we will notify you through the Services or by e-mail.
            </p>

            <h3>10. Contact Us</h3>
            <p>
                {{ legal.company }}<br/>
                {{ legal.mailingAddress }}<br/>
                {{ legal.phone }}<br/>
                <a :href="`mailto:${legal.supportEmail}`">{{ legal.supportEmail }}</a>
            </p>
        </template>
    </legal-page>
</template>

<script>
    import LegalPage from '@/components/legal/LegalPage';

    export default {
        name: 'Privacy',
        components: {LegalPage},
    };
</script>
